import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { WhopCheckoutEmbed } from "@whop/checkout/react";
import { Loader2, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";

const LOGO_URL = "https://customer-assets-4nw71qhi.emergentagent.net/job_paradise-shuttle-mx/artifacts/da39d5c78707bd62_Captura%20de%20pantalla%202026-10-09%20020512.png";
const X_LOGO_URL = "https://customer-assets-4nw71qhi.emergentagent.net/job_paradise-shuttle-mx/artifacts/2f6ae3642e6bb894_image.png";

const T = {
  es: {
    docTitle: "Pago — XCARET MEXICO",
    title: "Finaliza tu pago",
    customer: "Nombre de cliente",
    guests: "Huéspedes",
    transport: "Transportación",
    checkin: "Check In",
    checkout: "Check Out",
    total: "Total",
    secure: "Pago seguro procesado por Whop",
    loading: "Cargando pago seguro...",
    invalid: "Este link de pago no es válido o expiró.",
    adult: "adulto", adults: "adultos", minor: "menor", minors: "menores", infant: "infante", infants: "infantes",
  },
  en: {
    docTitle: "Payment — XCARET MEXICO",
    title: "Complete your payment",
    customer: "Customer name",
    guests: "Guests",
    transport: "Transportation",
    checkin: "Check-in",
    checkout: "Check-out",
    total: "Total",
    secure: "Secure payment processed by Whop",
    loading: "Loading secure checkout...",
    invalid: "This payment link is invalid or expired.",
    adult: "adult", adults: "adults", minor: "minor", minors: "minors", infant: "infant", infants: "infants",
  },
};

const guestsText = (info, t) => {
  const parts = [];
  if (info.adults) parts.push(`${info.adults} ${info.adults === 1 ? t.adult : t.adults}`);
  if (info.minors) parts.push(`${info.minors} ${info.minors === 1 ? t.minor : t.minors}`);
  if (info.infants) parts.push(`${info.infants} ${info.infants === 1 ? t.infant : t.infants}`);
  return parts.join(", ");
};

const fmtDate = (d, lang) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d || "");
  if (!m) return d;
  return lang === "en" ? `${m[2]}/${m[3]}/${m[1]}` : `${m[3]}/${m[2]}/${m[1]}`;
};

const Row = ({ label, value, testid }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex justify-between gap-4 py-2 font-body text-sm" data-testid={testid}>
      <span className="text-jungle-light/60">{label}</span>
      <span className="font-semibold text-jungle text-right">{value}</span>
    </div>
  );
};

export default function Pay() {
  const { orderId } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/pay/${orderId}`)
      .then(({ data }) => setInfo(data))
      .catch(() => setError(true));
  }, [orderId]);

  const lang = info?.lang === "en" ? "en" : "es";
  const t = T[lang];

  useEffect(() => { document.title = t.docTitle; }, [t]);

  if (error) {
    return (
      <div className="min-h-screen bg-sand flex flex-col items-center justify-center px-6 text-center">
        <span className="font-heading font-bold text-lg tracking-wide text-black">XCARET MEXICO</span>
        <p className="font-body text-jungle-light/70 mt-6">{T.es.invalid}</p>
      </div>
    );
  }
  if (!info) {
    return <div className="min-h-screen bg-sand flex items-center justify-center"><Loader2 className="animate-spin text-blue-600" size={32} /></div>;
  }

  const returnUrl = `${window.location.origin}/compra-exitosa`;
  const prefill = {
    email: info.email,
    name: info.name,
    phone: info.phone,
    address: { name: info.name },
  };
  const guests = guestsText(info, t);

  return (
    <div className="min-h-screen bg-sand" data-testid="pay-page">
      <header className="border-b border-border px-6 md:px-12 py-4 bg-sand">
        <div className="flex items-center gap-3">
          <img src={X_LOGO_URL} alt="XCARET MEXICO" className="h-9 w-9 object-contain" data-testid="pay-x-logo" />
          <span className="font-heading font-extrabold text-xl tracking-wide text-black" data-testid="pay-brand">XCARET MEXICO</span>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10 md:py-16">
        <p className="font-body text-xs tracking-[0.25em] uppercase text-blue-600 font-bold mb-6">{t.title}</p>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Información de la compra (izquierda / primero en móvil) */}
          <div>
            <img src={LOGO_URL} alt="XCARET MEXICO" className="h-24 mb-6 object-contain" data-testid="pay-logo-img" />
            <div className="bg-white border border-border p-6" data-testid="pay-summary">
              <h1 className="font-heading text-2xl font-semibold text-jungle mb-4">{info.package_name}</h1>
              <Row label={t.customer} value={info.name} testid="pay-customer" />
              <Row label={t.guests} value={guests} testid="pay-guests" />
              <Row label={t.transport} value={info.transport} testid="pay-transport" />
              <Row label={t.checkin} value={fmtDate(info.check_in, lang)} testid="pay-checkin" />
              <Row label={t.checkout} value={fmtDate(info.check_out, lang)} testid="pay-checkout" />
              <div className="border-t border-border mt-3 pt-4 flex items-center justify-between">
                <span className="font-body text-sm text-jungle-light/60">{t.total}</span>
                <span className="font-heading text-3xl font-bold text-blue-600" data-testid="pay-total">
                  ${(info.amount || 0).toLocaleString()}<span className="text-xs font-body font-normal text-jungle-light/60 ml-1">USD</span>
                </span>
              </div>
            </div>
          </div>

          {/* Pantalla de pago (derecha / abajo en móvil) */}
          <div>
            <div className="bg-white border border-border p-2 md:p-4" data-testid="pay-embed-container">
              <WhopCheckoutEmbed
                planId={info.plan_id}
                sessionId={info.checkout_id}
                prefill={prefill}
                returnUrl={returnUrl}
                theme="light"
                fallback={
                  <div className="flex flex-col items-center justify-center py-20 gap-3">
                    <Loader2 className="animate-spin text-blue-600" size={28} />
                    <span className="font-body text-sm text-jungle-light/70">{t.loading}</span>
                  </div>
                }
              />
            </div>
            <p className="flex items-center justify-center gap-1.5 font-body text-xs text-jungle-light/60 mt-4">
              <ShieldCheck size={13} className="text-blue-600" /> {t.secure}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
